// paymentService.js - Version Strapi
import strapiClient from './strapi/config';

const validateAmount = (amount) => {
    if (!amount || amount <= 0 || amount > 10000) {
        return false;
    }
    return true;
};

export const paymentService = {
    /**
     * Créer un Payment Intent via Strapi
     */
    validatePromo: async (code, atelierId) => {
        try {
            const response = await strapiClient.post('/api/payment/validate-promo', { code, atelierId });
            return response.data;
        } catch (error) {
            console.error('Erreur validation code promo:', error);
            return { valid: false, message: 'Erreur lors de la vérification du code' };
        }
    },

    createPaymentIntent: async (amount, productId, metadata = {}, promoCodeId = null) => {
        try {
            if (!validateAmount(amount)) {
                throw new Error(`Montant invalide: ${amount} (doit être entre 0.01 et 10000)`);
            }

            const amountInCents = Math.round(amount * 100);
            
            // Validation supplémentaire
            if (amountInCents < 50) {
                throw new Error(`Montant trop faible: ${amount}€ (minimum 0.50€)`);
            }
            
            // Log détaillé de la requête
            console.log('🔄 Creating payment intent request via Strapi:', { 
                originalAmount: amount,
                amountInCents: amountInCents, 
                productId,
                metadata,
                strapiUrl: process.env.REACT_APP_STRAPI_URL
            });
            
            // Vérifier que Strapi est configuré
            if (!process.env.REACT_APP_STRAPI_URL) {
                throw new Error('REACT_APP_STRAPI_URL non configuré');
            }
            
            // Appel à l'API Strapi (pas besoin d'auth pour créer un payment intent)
            const response = await strapiClient.post('/api/payment/create-payment-intent', {
                amount: amountInCents,
                productId,
                metadata,
                ...(promoCodeId && { promoCodeId })
            });
            
            console.log('📡 Strapi raw response:', response);
            
            // Validation de la réponse
            if (!response.data || !response.data.clientSecret) {
                console.error('❌ Invalid Strapi response:', response);
                throw new Error(`Réponse Strapi invalide: ${JSON.stringify(response.data)}`);
            }
            
            console.log('✅ Payment intent created successfully:', {
                clientSecret: response.data.clientSecret.substring(0, 25) + '...',
                paymentIntentId: response.data.paymentIntentId
            });
            
            return {
                clientSecret: response.data.clientSecret,
                paymentIntentId: response.data.paymentIntentId
            };

        } catch (error) {
            console.group('💥 Payment Intent Creation Error');
            console.error('Original error:', error);
            console.error('Error type:', typeof error);
            console.error('Error properties:', Object.keys(error));
            
            // Gestion spécifique des erreurs de connexion
            if (error.message?.includes('Network Error') || 
                error.code === 'ECONNREFUSED' ||
                (error.response === undefined && error.request)) {
                
                console.error('🚨 Connection error to Strapi detected');
                console.error('Strapi URL:', process.env.REACT_APP_STRAPI_URL);
                console.groupEnd();
                throw new Error(`Impossible de se connecter à Strapi (${process.env.REACT_APP_STRAPI_URL}). Vérifiez que Strapi est démarré.`);
            }
            
            if (error.response) {
                console.error('Response status:', error.response.status);
                console.error('Response data:', error.response.data);
                console.error('Response headers:', error.response.headers);
            }
            
            console.groupEnd();
            
            // Messages d'erreur plus précis
            if (error.response?.status === 400) {
                const errorMsg = error.response.data?.error?.message || 
                               error.response.data?.message ||
                               'Données de paiement invalides';
                throw new Error(`Erreur Strapi (400): ${errorMsg}`);
            } else if (error.response?.status === 500) {
                const errorMsg = error.response.data?.error?.message || 'Erreur serveur interne';
                throw new Error(`Erreur Strapi (500): ${errorMsg}`);
            } else if (error.response?.status === 404) {
                throw new Error('Route de paiement non trouvée. Vérifiez la configuration Strapi.');
            } else if (error.response?.status === 0 || !error.response) {
                throw new Error('Impossible de se connecter au serveur Strapi');
            }
            
            throw new Error(
                error.response?.data?.error?.message || 
                error.message || 
                'Erreur lors de l\'initialisation du paiement'
            );
        }
    },

    /**
     * Vérifier le statut d'un paiement via Strapi
     */
    checkPaymentStatus: async (paymentIntentId) => {
        try {
            const response = await strapiClient.get(`/api/payment/status/${paymentIntentId}`);
            return response.data;
        } catch (error) {
            console.error('Payment status check error:', error);
            throw new Error(
                error.response?.data?.error?.message || 
                'Erreur lors de la vérification du paiement'
            );
        }
    },

    /**
     * Formater un montant en euros
     */
    formatAmount: (amount) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR'
        }).format(amount);
    },

    /**
     * Tester la connexion à Strapi et la configuration Stripe
     */
    testConnection: async () => {
        const results = {
            strapiConnection: false,
            stripeConfiguration: false,
            paymentEndpoint: false,
            errors: []
        };

        try {
            console.log('🔍 Testing Strapi connection...');
            
            // Test de connexion basique à Strapi
            const healthResponse = await strapiClient.get('/_health');
            results.strapiConnection = true;
            console.log('✅ Strapi connection OK');

        } catch (error) {
            results.errors.push(`Strapi connection failed: ${error.message}`);
            console.error('❌ Strapi connection failed:', error.message);
        }

        try {
            console.log('🔍 Testing payment endpoint...');
            
            // Test de l'endpoint payment avec un montant test
            await paymentService.createPaymentIntent(25.00, 'test-connection');
            results.paymentEndpoint = true;
            results.stripeConfiguration = true;
            console.log('✅ Payment endpoint and Stripe configuration OK');

        } catch (error) {
            results.errors.push(`Payment endpoint failed: ${error.message}`);
            console.error('❌ Payment endpoint failed:', error.message);
        }

        return results;
    },

    /**
     * Sauvegarder les informations de paiement (optionnel)
     */
    savePaymentInfo: async (paymentData) => {
        try {
            // Cette fonction nécessite une authentification
            const token = localStorage.getItem('strapiToken');
            
            const response = await strapiClient.post('/api/payments', {
                data: paymentData
            }, {
                headers: token ? {
                    Authorization: `Bearer ${token}`
                } : {}
            });
            
            return response.data;
        } catch (error) {
            console.error('Error saving payment info:', error);
            // Ne pas faire échouer le paiement si la sauvegarde échoue
            return null;
        }
    }
};